import { templateFilterTriggerStyle, templateFilterPanelStyle, templateFilterItemStyle } from './template-discover-styles';
import React, { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BookmarkPlus, Check, ChevronDown, Folder, Loader2 } from 'lucide-react';
import type { TemplateWithBrands } from '@shared/templateCollections';
import type { Brand } from '@shared/schema';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

type TemplateSource = { winningAdId: number } | { templateId: number; brandIds?: number[] } |
  { imageUrl: string } | { videoUrl: string; thumbnailUrl?: string | null; name?: string };

function useDestinationBrands(open: boolean) {
  // Refresh on every open: a newly created brand is immediately a destination,
  // including when another surface created it without invalidating this cache.
  return useQuery<Brand[]>({ queryKey: ['/api/brands'], enabled: open, staleTime: 0 });
}

export function TemplateBrandFilter({ value, onChange, open, onOpenChange }: {
  value: string; onChange: (value: string) => void; open: boolean; onOpenChange: (open: boolean) => void;
}) {
  const { data: brands = [], isLoading, isError, refetch } = useDestinationBrands(open);
  const label = value === 'all' ? 'All Templates' : brands.find(brand => String(brand.id) === value)?.name ?? 'Brand';
  const choices = [{ id: 'all', name: 'All Templates' }, ...brands.map(brand => ({ id: String(brand.id), name: brand.name }))];
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button data-testid="button-template-brand-filter" style={templateFilterTriggerStyle} className="hover:!bg-[#F0EDE8]" aria-label={`Brand: ${label}`}>
          <Folder size={12} />{label}<ChevronDown size={11} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} data-testid="template-brand-filter-menu" style={{ ...templateFilterPanelStyle, padding: '6px 0', minWidth: 200, overflowY: 'auto', maxHeight: 320 }}>
        {choices.map(choice => <DropdownMenuItem key={choice.id}
          style={{ ...templateFilterItemStyle, color: value === choice.id ? '#1A1A1A' : '#3D3A36', fontWeight: value === choice.id ? 500 : 400 }}
          className="focus:!bg-[#F5F3EE]" onSelect={() => onChange(choice.id)}>
          <Folder style={{ width: 12, height: 12, color: '#8C8578' }} />{choice.name}{value === choice.id && <Check style={{ width: 12, height: 12, marginLeft: 'auto' }} />}
        </DropdownMenuItem>)}
        {isLoading && <DropdownMenuItem style={templateFilterItemStyle} disabled>Loading brands…</DropdownMenuItem>}
        {isError && <DropdownMenuItem style={templateFilterItemStyle} onSelect={(event) => { event.preventDefault(); void refetch(); }}>Couldn’t load brands. Retry</DropdownMenuItem>}
        {!isLoading && !isError && brands.length === 0 && <DropdownMenuItem style={templateFilterItemStyle} disabled>No brands yet</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SaveTemplateBrandMenu({ source, testId, iconOnly = false, style, className, label, preferredBrandId }: {
  preferredBrandId?: number | null; source: TemplateSource; testId: string; iconOnly?: boolean; style?: React.CSSProperties; className?: string; label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const busy = useRef(false);
  const { data: brands = [], isLoading, isError, refetch } = useDestinationBrands(open);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: cachedTemplates } = useQuery<TemplateWithBrands[]>({queryKey:['/api/ad-templates'],enabled:false});
  const sourceKey = 'winningAdId' in source ? `winning:${source.winningAdId}` : 'templateId' in source ? `id:${source.templateId}` : 'imageUrl' in source ? `image:${source.imageUrl}` : `video:${source.videoUrl}`;
  const aliasKey = ['template-source-membership', sourceKey];
  const { data: imported } = useQuery<{id:number;brandIds:number[]}>({queryKey:aliasKey,enabled:false});
  const existingId = 'templateId' in source ? source.templateId : imported?.id;
  const cached = cachedTemplates?.find(template=>template.id===existingId);
  const memberships = new Set(cached?.brandIds ?? imported?.brandIds ?? (('brandIds' in source && source.brandIds) || []));
  const saved = memberships.size > 0;

  async function save(brandId: number) {
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    try {
      const removing = !('winningAdId' in source) && memberships.has(brandId);
      if ('winningAdId' in source && memberships.has(brandId)) { setOpen(false); return; }
      const url = 'winningAdId' in source ? `/api/discover/competitors/${source.winningAdId}/save` : existingId != null ? `/api/ad-templates/${existingId}/brands${removing ? `/${brandId}` : ''}` : '/api/ad-templates/from-url';
      const body = existingId != null ? { brandId } : { ...source, brandId };
      const response = await fetch(url, { method: removing ? 'DELETE' : 'POST', headers: { 'Content-Type': 'application/json' }, ...(removing ? {} : {body: JSON.stringify(body)}), credentials: 'include' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.error || 'Failed to save template');
      const id = existingId ?? result.id;
      const brandIds = removing ? Array.from(memberships).filter(value=>value!==brandId) : Array.from(new Set([...Array.from(memberships),brandId]));
      // Membership edits do not reload or reorder the library. Other card/menu instances share this state.
      await queryClient.cancelQueries({queryKey:['/api/ad-templates']});
      queryClient.setQueryData(aliasKey,{id,brandIds});
      queryClient.setQueriesData<TemplateWithBrands[]>({queryKey:['/api/ad-templates']},old=> {
        if(!old)return old;
        return old.some(template=>template.id===id) ? old.map(template=>template.id===id?{...template,brandIds}:template) : existingId == null ? [...old,{...result,brandIds}] : old;
      });
      toast({ title: removing ? 'Template removed from brand' : 'Template saved', description: `${removing ? 'Removed from' : 'Saved to'} ${brands.find(brand => brand.id === brandId)?.name ?? 'your brand'}.` });
      setOpen(false);
    } catch (error) {
      toast({ title: 'Save failed', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' });
    } finally { busy.current = false; setSaving(false); }
  }

  return (
    <span onClick={event => event.stopPropagation()} onPointerDown={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()} style={{ display: iconOnly ? 'inline-flex' : 'block', width: iconOnly ? undefined : '100%' }}>
      <DropdownMenu open={open} onOpenChange={next => { if (!saving) { if(next && preferredBrandId && 'winningAdId' in source) void save(preferredBrandId); else setOpen(next); } }}>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid={testId} title="Save to brand" aria-label={iconOnly ? 'Save template to brand' : undefined} disabled={saving} className={className}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: iconOnly ? 26 : '100%', height: iconOnly ? 26 : undefined, padding: iconOnly ? 0 : '8px 0', borderRadius: 6, border: iconOnly ? '1px solid #E5E2DC' : 'none', background: iconOnly ? 'rgba(255,255,255,0.95)' : 'transparent', color: saved ? '#5E8E6E' : '#6B6760', fontSize: 12, fontWeight: 500, cursor: saving ? 'wait' : 'pointer', ...style }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : <BookmarkPlus size={13} />}
            {!iconOnly && <>{saving ? 'Saving…' : saved ? 'Saved to Templates' : (label ?? 'Save to Templates')}<ChevronDown size={11} /></>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={iconOnly ? 'end' : 'start'} sideOffset={6} style={{ ...templateFilterPanelStyle, padding: '6px 0', minWidth: 200, overflowY: 'auto', maxHeight: 320 }} onClick={event => event.stopPropagation()}>
          {isLoading && <DropdownMenuItem style={templateFilterItemStyle} disabled>Loading brands…</DropdownMenuItem>}
          {isError && <DropdownMenuItem style={templateFilterItemStyle} onSelect={event => { event.preventDefault(); void refetch(); }}>Couldn’t load brands. Retry</DropdownMenuItem>}
          {brands.map(brand => <DropdownMenuItem key={brand.id} style={templateFilterItemStyle} className="focus:!bg-[#F5F3EE]" disabled={saving} onSelect={event => { event.preventDefault(); void save(brand.id); }}>
            <Folder style={{ width: 12, height: 12, color: '#8C8578' }} />{brand.name}{memberships.has(brand.id) && <Check className="ml-auto" size={12} />}
          </DropdownMenuItem>)}
          {!isLoading && !isError && brands.length === 0 && <DropdownMenuItem style={templateFilterItemStyle} disabled>Create a brand to organize templates</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
